import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  ChevronDown,
  CircleDollarSign,
  LayoutGrid,
  PieChart,
  Plus,
  Search,
  Target,
  Trash2,
  Upload,
  Users,
  Building2,
} from 'lucide-react';
import { FormDrawer } from '../../../forms';
import { useUIColors } from '../../../../hooks/useTheme';
import {
  getFinancingActivityActionOptions,
  getFinancingActivityStatusOptions,
  getFinancingFunnelStageOptions,
  getFundInstrumentOptions,
  getFunnelStageColor,
  getContactRoleOptions,
  getInvestmentStageOptions,
  getInvestorTypeOptions,
  getLeadRoleOptions,
} from '../../../placeholders/financingCatalogDefaults';
import {
  FinancingContactsBulkImportModal,
  FinancingEntitiesBulkImportModal,
  downloadFinancingContactsTemplate,
  downloadFinancingEntitiesTemplate,
} from './FinancingBulkImportModals';
import FinancingFundraisingViews, {
  useFinancingFundraising,
} from './FinancingFundraisingViews';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const EMPTY_ENTITY = {
  name: '',
  business_name: '',
  rut: '',
  entity_type: '',
  email: '',
  phone: '',
  website: '',
  linkedin: '',
  address: '',
  commune: '',
  region: '',
  country: 'Chile',
  funnel_stage: 'Identificado',
  status: 'Activo',
  target_amount: '',
  committed_amount: '',
  notes: '',
  agency: '',
  instrument: '',
  application_deadline: '',
  investment_thesis: '',
  sectors: '',
  preferred_stages: '',
  ticket_min: '',
  ticket_max: '',
  geography: '',
  aum: '',
  lead_role: '',
  portfolio_url: '',
  entry_round: '',
  contacts: [],
};

const EMPTY_CONTACT = {
  full_name: '',
  role: '',
  email: '',
  phone: '',
  linkedin: '',
  parent_id: '',
  status: 'Activo',
};

const EMPTY_INLINE_CONTACT = {
  id: null,
  full_name: '',
  role: '',
  email: '',
  phone: '',
  linkedin: '',
  status: 'Activo',
};

const EMPTY_ACTIVITY = {
  action: 'Llamada',
  activity_date: '',
  status: 'Pendiente',
  notes: '',
  response: '',
  next_step: '',
  next_step_date: '',
  has_next_steps: false,
  has_follow_up_reminder: false,
  follow_up_reminder_date: '',
  funnel_stage: '',
  owner_name: '',
  contact_id: '',
  investment_round: '',
  entry_amount: '',
};

const btnPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 14px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary = (ui, isDarkMode) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: `1px solid ${ui.inputBorder}`,
  background: isDarkMode ? '#27272a' : '#fff',
  color: ui.text,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
});

const SimpleTable = ({
  columns,
  rows,
  loading,
  error,
  isDarkMode,
  ui,
  onRowClick,
  selectedIds,
  onToggleSelect,
  search,
  onSearch,
  title,
  subtitle,
  toolbar,
  hideChrome = false,
  showSearch = true,
}) => (
  <div
    style={
      hideChrome
        ? { background: ui.page }
        : {
            border: `1px solid ${ui.border}`,
            borderRadius: 12,
            overflow: 'hidden',
            background: ui.page,
          }
    }
  >
    {!hideChrome ? (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>{title}</h3>
          <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{subtitle}</p>
        </div>
        <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>{toolbar}</div>
      </div>
    ) : null}
    {showSearch ? (
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${ui.border}` }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={16} color={ui.muted} />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Buscar…"
            style={{
              flex: 1,
              height: 36,
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder}`,
              background: ui.inputBg,
              color: ui.text,
              padding: '0 10px',
              fontSize: 13,
            }}
          />
        </span>
      </div>
    ) : null}
    <div style={{ overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc', textAlign: 'left' }}>
            {onToggleSelect ? (
              <th style={{ padding: '8px 10px', width: 36 }}>
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selectedIds.length === rows.length}
                  onChange={(e) =>
                    onToggleSelect(e.target.checked ? rows.map((r) => r.id) : [])
                  }
                />
              </th>
            ) : null}
            {columns.map((col) => (
              <th key={col.key} style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: ui.muted }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + (onToggleSelect ? 1 : 0)} style={{ padding: 16, color: ui.muted }}>
                Cargando…
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={columns.length + (onToggleSelect ? 1 : 0)} style={{ padding: 16, color: '#dc2626' }}>
                {error}
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (onToggleSelect ? 1 : 0)} style={{ padding: 16, color: ui.muted }}>
                Sin registros.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={row.id}
                style={{ borderTop: `1px solid ${ui.border}`, cursor: onRowClick ? 'pointer' : 'default' }}
                onClick={() => onRowClick?.(row)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = ui.rowHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {onToggleSelect ? (
                  <td style={{ padding: '8px 10px' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={(e) => {
                        if (e.target.checked) onToggleSelect([...selectedIds, row.id]);
                        else onToggleSelect(selectedIds.filter((id) => id !== row.id));
                      }}
                    />
                  </td>
                ) : null}
                {columns.map((col) => (
                  <td key={col.key} style={{ padding: '8px 10px', color: ui.text }}>
                    {col.render ? col.render(row) : row[col.key] || '—'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const FinancingTrackPanel = ({
  trackKey = 'investors',
  api,
  isDarkMode = false,
  entityLabel = 'Inversionista',
  entitiesLabel = 'Inversionistas',
  parentNameField = 'parent_name',
  onDataMutated,
  openEntityRequest = null,
}) => {
  const ui = useUIColors();
  const isFund = trackKey === 'public_funds';
  const funnelOptions = useMemo(() => getFinancingFunnelStageOptions(), []);
  const typeOptions = useMemo(
    () => (isFund ? getFundInstrumentOptions() : getInvestorTypeOptions()),
    [isFund]
  );
  const stageOptions = useMemo(() => getInvestmentStageOptions(), []);
  const leadRoleOptions = useMemo(() => getLeadRoleOptions(), []);
  const contactRoleOptions = useMemo(
    () => getContactRoleOptions(isFund ? 'fund' : 'investor'),
    [isFund]
  );
  const actionOptions = useMemo(() => getFinancingActivityActionOptions(), []);
  const activityStatusOptions = useMemo(() => getFinancingActivityStatusOptions(), []);

  const fundraising = useFinancingFundraising({
    enabled: !isFund,
    onDataMutated,
  });

  const [listTab, setListTab] = useState('entities');
  const [entities, setEntities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tracking, setTracking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchEntities, setSearchEntities] = useState('');
  const [searchContacts, setSearchContacts] = useState('');
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  const [showEntityForm, setShowEntityForm] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [entityForm, setEntityForm] = useState({ ...EMPTY_ENTITY });
  const [entityOriginalContactIds, setEntityOriginalContactIds] = useState([]);
  const [entityFormError, setEntityFormError] = useState('');
  const [savingEntity, setSavingEntity] = useState(false);

  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm, setContactForm] = useState({ ...EMPTY_CONTACT });
  const [contactFormError, setContactFormError] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  const [detailEntity, setDetailEntity] = useState(null);
  const [detailActivities, setDetailActivities] = useState([]);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityForm, setActivityForm] = useState({ ...EMPTY_ACTIVITY });
  const [savingActivity, setSavingActivity] = useState(false);
  const [activityError, setActivityError] = useState('');

  const [showEntityBulk, setShowEntityBulk] = useState(false);
  const [showContactBulk, setShowContactBulk] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [notice, setNotice] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ents, cts, tr] = await Promise.all([
        api.getAll(),
        api.getContacts(),
        api.getTracking(),
      ]);
      const nextEntities = Array.isArray(ents) ? ents : [];
      const nextContacts = Array.isArray(cts) ? cts : [];
      const nextTracking = Array.isArray(tr) ? tr : [];
      setEntities(nextEntities);
      setContacts(nextContacts);
      setTracking(nextTracking);
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar los datos.');
      setEntities([]);
      setContacts([]);
      setTracking([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const reloadAndNotify = useCallback(async () => {
    await loadAll();
    onDataMutated?.();
  }, [loadAll, onDataMutated]);

  const entitySelectOptions = useMemo(
    () =>
      entities.map((e) => ({
        value: String(e.id),
        label: e.name,
      })),
    [entities]
  );

  const filteredEntities = useMemo(() => {
    const q = searchEntities.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter((row) =>
      [row.name, row.business_name, row.rut, row.entity_type, row.funnel_stage, row.status]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [entities, searchEntities]);

  const filteredContacts = useMemo(() => {
    const q = searchContacts.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((row) =>
      [row.full_name, row.email, row.phone, row.role, row.parent_name || row[parentNameField]]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [contacts, searchContacts, parentNameField]);

  const pipelineColumns = useMemo(() => {
    return funnelOptions
      .filter((o) => o.value !== 'Perdido')
      .map((stage) => ({
        ...stage,
        cards: entities.filter((e) => (e.funnel_stage || 'Identificado') === stage.value),
      }));
  }, [funnelOptions, entities]);

  const contactsForEntity = useCallback(
    (entityId) =>
      contacts
        .filter((c) => Number(c.investor_id || c.fund_id || c.parent_id) === Number(entityId))
        .map((c) => ({
          id: c.id,
          full_name: c.full_name || '',
          role: c.role || '',
          email: c.email || '',
          phone: c.phone || '',
          linkedin: c.linkedin || '',
          status: c.status || 'Activo',
        })),
    [contacts]
  );

  const updateInlineContact = (index, key, value) => {
    setEntityForm((prev) => {
      const list = Array.isArray(prev.contacts) ? [...prev.contacts] : [];
      list[index] = { ...list[index], [key]: value };
      return { ...prev, contacts: list };
    });
  };

  const addInlineContact = () => {
    setEntityForm((prev) => ({
      ...prev,
      contacts: [...(Array.isArray(prev.contacts) ? prev.contacts : []), { ...EMPTY_INLINE_CONTACT }],
    }));
  };

  const removeInlineContact = (index) => {
    setEntityForm((prev) => ({
      ...prev,
      contacts: (Array.isArray(prev.contacts) ? prev.contacts : []).filter((_, i) => i !== index),
    }));
  };

  const openCreateEntity = () => {
    setEditingEntityId(null);
    setEntityForm({ ...EMPTY_ENTITY, contacts: [] });
    setEntityOriginalContactIds([]);
    setEntityFormError('');
    setShowEntityForm(true);
  };

  const openEditEntity = (row) => {
    const existing = contactsForEntity(row.id);
    setEditingEntityId(row.id);
    setEntityForm({
      ...EMPTY_ENTITY,
      ...row,
      target_amount: row.target_amount ?? '',
      committed_amount: row.committed_amount ?? '',
      ticket_min: row.ticket_min ?? '',
      ticket_max: row.ticket_max ?? '',
      aum: row.aum ?? '',
      application_deadline: row.application_deadline
        ? String(row.application_deadline).slice(0, 10)
        : '',
      contacts: existing,
    });
    setEntityOriginalContactIds(existing.map((c) => Number(c.id)).filter(Boolean));
    setEntityFormError('');
    setShowEntityForm(true);
  };

  const saveEntity = async () => {
    if (!String(entityForm.name || '').trim()) {
      setEntityFormError('El nombre es obligatorio.');
      return;
    }
    const contactsPayload = (Array.isArray(entityForm.contacts) ? entityForm.contacts : [])
      .map((c) => ({
        id: c.id ? Number(c.id) : null,
        full_name: String(c.full_name || '').trim(),
        role: String(c.role || '').trim(),
        email: String(c.email || '').trim(),
        phone: String(c.phone || '').trim(),
        linkedin: String(c.linkedin || '').trim(),
        status: String(c.status || 'Activo').trim() || 'Activo',
      }))
      .filter((c) => c.full_name);

    setSavingEntity(true);
    setEntityFormError('');
    const { contacts: _omitContacts, ...entityFields } = entityForm;
    const payload = {
      ...entityFields,
      target_amount: Number(entityForm.target_amount || 0),
      committed_amount: Number(entityForm.committed_amount || 0),
      ticket_min: Number(entityForm.ticket_min || 0),
      ticket_max: Number(entityForm.ticket_max || 0),
      aum: Number(entityForm.aum || 0),
      application_deadline: entityForm.application_deadline || null,
    };
    try {
      let entityId = editingEntityId;
      if (editingEntityId) {
        await api.update(editingEntityId, payload);
      } else {
        const created = await api.create(payload);
        entityId = Number(created?.id || 0);
      }
      if (!entityId) throw new Error('No se pudo determinar el ID para guardar contactos.');

      const keptIds = new Set();
      for (const contact of contactsPayload) {
        const contactBody = {
          full_name: contact.full_name,
          role: contact.role,
          email: contact.email,
          phone: contact.phone,
          linkedin: contact.linkedin,
          status: contact.status,
          parent_id: entityId,
        };
        if (contact.id) {
          keptIds.add(Number(contact.id));
          await api.updateContact(contact.id, contactBody);
        } else {
          await api.createContact(contactBody);
        }
      }
      const toDelete = entityOriginalContactIds.filter((id) => !keptIds.has(Number(id)));
      for (const contactId of toDelete) {
        await api.deleteContact(contactId);
      }

      setShowEntityForm(false);
      await reloadAndNotify();
    } catch (err) {
      setEntityFormError(err?.message || 'No se pudo guardar.');
    } finally {
      setSavingEntity(false);
    }
  };

  const openCreateContact = () => {
    setEditingContactId(null);
    setContactForm({ ...EMPTY_CONTACT });
    setContactFormError('');
    setShowContactForm(true);
  };

  const openEditContact = (row) => {
    setEditingContactId(row.id);
    setContactForm({
      full_name: row.full_name || '',
      role: row.role || '',
      email: row.email || '',
      phone: row.phone || '',
      linkedin: row.linkedin || '',
      parent_id: row.investor_id || row.fund_id || row.parent_id || '',
      status: row.status || 'Activo',
    });
    setContactFormError('');
    setShowContactForm(true);
  };

  const saveContact = async () => {
    if (!String(contactForm.full_name || '').trim()) {
      setContactFormError('El nombre es obligatorio.');
      return;
    }
    if (!Number(contactForm.parent_id)) {
      setContactFormError(`Debes seleccionar un ${entityLabel.toLowerCase()}.`);
      return;
    }
    setSavingContact(true);
    setContactFormError('');
    try {
      const payload = {
        ...contactForm,
        parent_id: Number(contactForm.parent_id),
      };
      if (editingContactId) await api.updateContact(editingContactId, payload);
      else await api.createContact(payload);
      setShowContactForm(false);
      await reloadAndNotify();
    } catch (err) {
      setContactFormError(err?.message || 'No se pudo guardar.');
    } finally {
      setSavingContact(false);
    }
  };

  const openDetail = async (row) => {
    if (!row) return;
    setDetailEntity(row);
    setListTab('entities');
    try {
      const acts = await api.getActivities(row.id);
      setDetailActivities(Array.isArray(acts) ? acts : []);
    } catch {
      setDetailActivities([]);
    }
  };

  useEffect(() => {
    const requestId = Number(openEntityRequest?.id || 0);
    const token = openEntityRequest?.token;
    if (!requestId || !token || !entities.length) return;
    const ent = entities.find((e) => Number(e.id) === requestId);
    if (ent) openDetail(ent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEntityRequest, entities]);

  const saveActivity = async () => {
    if (!detailEntity?.id) return;
    setSavingActivity(true);
    setActivityError('');
    try {
      const payload = {
        ...activityForm,
        contact_id: activityForm.contact_id ? Number(activityForm.contact_id) : null,
        contact_ids: activityForm.contact_id ? [Number(activityForm.contact_id)] : [],
        has_next_steps: Boolean(activityForm.next_step || activityForm.next_step_date),
        has_follow_up_reminder: Boolean(activityForm.follow_up_reminder_date),
        activity_date: activityForm.activity_date || null,
        next_step_date: activityForm.next_step_date || null,
        follow_up_reminder_date: activityForm.follow_up_reminder_date || null,
        investment_round: activityForm.investment_round || '',
        entry_amount: Number(activityForm.entry_amount || 0),
      };
      const entityId = detailEntity.id;
      await api.createActivity(entityId, payload);
      setShowActivityForm(false);
      setActivityForm({ ...EMPTY_ACTIVITY });
      await reloadAndNotify();
      const [acts, refreshed] = await Promise.all([
        api.getActivities(entityId),
        api.getById ? api.getById(entityId) : Promise.resolve(null),
      ]);
      setDetailActivities(Array.isArray(acts) ? acts : []);
      if (refreshed?.id) setDetailEntity(refreshed);
    } catch (err) {
      setActivityError(err?.message || 'No se pudo guardar la actividad.');
    } finally {
      setSavingActivity(false);
    }
  };

  const entityColumns = [
    {
      key: 'code',
      label: 'Código',
      render: (r) => r.investor_code || r.fund_code || '—',
    },
    { key: 'name', label: 'Nombre' },
    { key: 'entity_type', label: 'Tipo' },
    {
      key: 'funnel_stage',
      label: 'Etapa',
      render: (r) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 999,
            background: `${getFunnelStageColor(r.funnel_stage)}22`,
            color: getFunnelStageColor(r.funnel_stage),
            fontWeight: 600,
          }}
        >
          {r.funnel_stage || '—'}
        </span>
      ),
    },
    {
      key: 'target_amount',
      label: 'Objetivo',
      render: (r) => formatMoney(r.target_amount),
    },
    ...(!isFund
      ? [
          {
            key: 'entry_round',
            label: 'Ronda',
            render: (r) => r.entry_round || '—',
          },
          {
            key: 'committed_amount',
            label: 'Entrada',
            render: (r) => (Number(r.committed_amount) ? formatMoney(r.committed_amount) : '—'),
          },
        ]
      : []),
    { key: 'status', label: 'Estado' },
  ];

  if (isFund) {
    entityColumns.splice(3, 0, { key: 'agency', label: 'Agencia' });
  }

  const contactColumns = [
    { key: 'contact_code', label: 'Código' },
    { key: 'full_name', label: 'Contacto' },
    {
      key: 'parent_name',
      label: entityLabel,
      render: (r) => r.parent_name || '—',
    },
    { key: 'email', label: 'Correo' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'status', label: 'Estado' },
  ];

  const entityFormSections = [
    {
      key: 'basic',
      title: entityLabel,
      fields: [
        { key: 'name', label: 'Nombre / fondo' },
        { key: 'business_name', label: 'Razón social' },
        { key: 'rut', label: 'RUT' },
        {
          key: 'entity_type',
          label: isFund ? 'Instrumento' : 'Tipo de inversionista',
          type: 'select',
          options: typeOptions,
        },
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'phone', label: 'Teléfono' },
        { key: 'website', label: 'Sitio web' },
        { key: 'linkedin', label: 'LinkedIn' },
        { key: 'address', label: 'Dirección' },
        { key: 'commune', label: 'Comuna' },
        { key: 'region', label: 'Región' },
        {
          key: 'funnel_stage',
          label: 'Etapa de prospección',
          type: 'select',
          options: funnelOptions,
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: [
            { value: 'Activo', label: 'Activo' },
            { value: 'Inactivo', label: 'Inactivo' },
          ],
        },
        { key: 'target_amount', label: 'Monto objetivo (CLP)', type: 'number' },
        {
          key: 'committed_amount',
          label: isFund ? 'Monto comprometido (CLP)' : 'Monto de entrada / comprometido (CLP)',
          type: 'number',
        },
        ...(!isFund
          ? [
              {
                key: 'entry_round',
                label: 'Ronda de entrada',
                type: 'select',
                options: [{ value: '', label: 'Sin ronda aún…' }, ...stageOptions],
              },
            ]
          : []),
        ...(isFund
          ? [
              { key: 'agency', label: 'Agencia / institución' },
              {
                key: 'instrument',
                label: 'Instrumento',
                type: 'select',
                options: typeOptions,
              },
              { key: 'application_deadline', label: 'Plazo postulación', type: 'date' },
            ]
          : []),
      ],
    },
    ...(!isFund
      ? [
          {
            key: 'investor_profile',
            title: 'Perfil de inversión (VC / otros)',
            fields: [
              {
                key: 'investment_thesis',
                label: 'Tesis de inversión',
                type: 'textarea',
                fullWidth: true,
                placeholder: 'Qué buscan, cheque size típico, criterios de entrada…',
              },
              {
                key: 'sectors',
                label: 'Sectores / industrias',
                placeholder: 'Ej. SaaS, fintech, clima, healthtech',
              },
              {
                key: 'preferred_stages',
                label: 'Etapa preferida (tesis del fondo)',
                type: 'select',
                options: stageOptions,
              },
              {
                key: 'lead_role',
                label: 'Rol típico en ronda',
                type: 'select',
                options: leadRoleOptions,
              },
              { key: 'ticket_min', label: 'Ticket mínimo (CLP)', type: 'number' },
              { key: 'ticket_max', label: 'Ticket máximo (CLP)', type: 'number' },
              { key: 'aum', label: 'AUM / capital bajo gestión (CLP)', type: 'number' },
              {
                key: 'geography',
                label: 'Foco geográfico',
                placeholder: 'Ej. Chile, LatAm, global',
              },
              { key: 'portfolio_url', label: 'URL portafolio' },
            ],
          },
        ]
      : []),
    {
      key: 'contacts',
      title: 'Contactos',
      fields: [
        {
          key: 'contacts',
          fullWidth: true,
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => {
            const localContacts = Array.isArray(value) ? value : [];
            const roleSelectStyle = { ...baseInputStyle, appearance: 'auto' };
            return (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: colors.muted }}>
                    Agrega contactos clave del {entityLabel.toLowerCase()}.
                  </span>
                  <button
                    type="button"
                    onClick={addInlineContact}
                    style={{
                      height: 30,
                      borderRadius: 8,
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      padding: '0 10px',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    + Agregar contacto
                  </button>
                </div>
                {!localContacts.length ? (
                  <div style={{ fontSize: 12, color: colors.muted, padding: '8px 0' }}>
                    Sin contactos agregados.
                  </div>
                ) : null}
                {localContacts.map((contact, index) => (
                  <div
                    key={contact.id ? `contact-${contact.id}` : `contact-new-${index}`}
                    style={{
                      border: `1px solid ${colors.inputBorder}`,
                      borderRadius: 8,
                      padding: 10,
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: 12, color: colors.text }}>Contacto {index + 1}</strong>
                      <button
                        type="button"
                        onClick={() => removeInlineContact(index)}
                        style={{
                          height: 28,
                          borderRadius: 8,
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: colors.text,
                          padding: '0 8px',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                      <input
                        style={baseInputStyle}
                        placeholder="Nombre completo"
                        value={contact.full_name || ''}
                        onChange={(e) => updateInlineContact(index, 'full_name', e.target.value)}
                      />
                      <select
                        style={roleSelectStyle}
                        value={contact.role || ''}
                        onChange={(e) => updateInlineContact(index, 'role', e.target.value)}
                      >
                        <option value="">Cargo…</option>
                        {contactRoleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                        {contact.role &&
                        !contactRoleOptions.some((opt) => opt.value === contact.role) ? (
                          <option value={contact.role}>{contact.role}</option>
                        ) : null}
                      </select>
                      <input
                        style={baseInputStyle}
                        placeholder="Correo"
                        type="email"
                        value={contact.email || ''}
                        onChange={(e) => updateInlineContact(index, 'email', e.target.value)}
                      />
                      <input
                        style={baseInputStyle}
                        placeholder="Teléfono"
                        value={contact.phone || ''}
                        onChange={(e) => updateInlineContact(index, 'phone', e.target.value)}
                      />
                      <input
                        style={{ ...baseInputStyle, gridColumn: '1 / -1' }}
                        placeholder="LinkedIn"
                        value={contact.linkedin || ''}
                        onChange={(e) => updateInlineContact(index, 'linkedin', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          },
        },
      ],
    },
    {
      key: 'notes_section',
      title: 'Notas',
      fields: [{ key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true }],
    },
  ];

  const contactFormSections = [
    {
      key: 'basic',
      title: 'Contacto',
      fields: [
        { key: 'full_name', label: 'Nombre' },
        {
          key: 'role',
          label: 'Cargo',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar cargo…' }, ...contactRoleOptions],
        },
        { key: 'email', label: 'Correo', type: 'email' },
        { key: 'phone', label: 'Teléfono' },
        { key: 'linkedin', label: 'LinkedIn' },
        {
          key: 'parent_id',
          label: entityLabel,
          type: 'select',
          options: entitySelectOptions,
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: [
            { value: 'Activo', label: 'Activo' },
            { value: 'Inactivo', label: 'Inactivo' },
          ],
        },
      ],
    },
  ];

  const activityFormSections = [
    {
      key: 'basic',
      title: 'Actividad',
      fields: [
        {
          key: 'action',
          label: 'Acción',
          type: 'select',
          options: actionOptions,
        },
        { key: 'activity_date', label: 'Fecha', type: 'date' },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: activityStatusOptions,
        },
        {
          key: 'funnel_stage',
          label: 'Etapa funnel',
          type: 'select',
          options: [{ value: '', label: '(sin cambio)' }, ...funnelOptions],
        },
        ...(!isFund
          ? [
              {
                key: 'investment_round',
                label: 'Ronda de entrada',
                type: 'select',
                options: [{ value: '', label: '(sin cambio)' }, ...stageOptions],
              },
              {
                key: 'entry_amount',
                label: 'Monto de entrada (CLP)',
                type: 'number',
              },
            ]
          : []),
        {
          key: 'contact_id',
          label: 'Contacto',
          type: 'select',
          options: [
            { value: '', label: '(sin contacto)' },
            ...contacts
              .filter((c) => Number(c.investor_id || c.fund_id) === Number(detailEntity?.id))
              .map((c) => ({ value: String(c.id), label: c.full_name })),
          ],
        },
        { key: 'owner_name', label: 'Responsable' },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true },
        { key: 'next_step', label: 'Próximo paso' },
        { key: 'next_step_date', label: 'Fecha próximo paso', type: 'date' },
        { key: 'follow_up_reminder_date', label: 'Recordatorio', type: 'date' },
      ],
    },
  ];

  const listTabs = [
    { key: 'entities', label: entitiesLabel, count: entities.length, icon: Building2 },
    { key: 'contacts', label: 'Contactos', count: contacts.length, icon: Users },
    ...(!isFund
      ? [
          {
            key: 'rounds',
            label: 'Rondas',
            count: fundraising.rounds.length,
            icon: Target,
          },
          {
            key: 'investments',
            label: 'Inversiones',
            count: fundraising.investments.length,
            icon: CircleDollarSign,
          },
          {
            key: 'cap-table',
            label: 'Cap Table',
            count: fundraising.capTable.length,
            icon: PieChart,
          },
        ]
      : []),
    { key: 'seguimiento', label: 'Seguimiento', count: tracking.length, icon: CalendarDays },
    { key: 'pipeline', label: 'Pipeline', count: entities.length, icon: LayoutGrid },
  ];

  const headerToolbar =
    listTab === 'entities' ? (
      <>
        <button type="button" style={btnPrimary} onClick={openCreateEntity}>
          <Plus size={16} /> Nuevo
        </button>
        <div style={{ display: 'inline-flex' }}>
          <button
            type="button"
            style={{ ...btnSecondary(ui, isDarkMode), borderRadius: '8px 0 0 8px' }}
            onClick={() => {
              setBulkResult(null);
              setShowEntityBulk(true);
            }}
          >
            <Upload size={16} /> Carga masiva
          </button>
          <button
            type="button"
            title="Descargar plantilla"
            style={{
              ...btnSecondary(ui, isDarkMode),
              borderRadius: '0 8px 8px 0',
              borderLeft: 'none',
              width: 34,
              padding: 0,
              justifyContent: 'center',
            }}
            onClick={() =>
              downloadFinancingEntitiesTemplate({
                trackKey,
                sheetName: entitiesLabel,
              })
            }
          >
            <ChevronDown size={16} />
          </button>
        </div>
        {selectedEntityIds.length ? (
          <button
            type="button"
            style={{ ...btnSecondary(ui, isDarkMode), color: '#dc2626' }}
            onClick={async () => {
              if (!window.confirm(`¿Eliminar ${selectedEntityIds.length} registro(s)?`)) return;
              await api.bulkDelete(selectedEntityIds);
              setSelectedEntityIds([]);
              await reloadAndNotify();
            }}
          >
            <Trash2 size={16} /> Eliminar
          </button>
        ) : null}
      </>
    ) : listTab === 'contacts' ? (
      <>
        <button type="button" style={btnPrimary} onClick={openCreateContact}>
          <Plus size={16} /> Nuevo contacto
        </button>
        <div style={{ display: 'inline-flex' }}>
          <button
            type="button"
            style={{ ...btnSecondary(ui, isDarkMode), borderRadius: '8px 0 0 8px' }}
            onClick={() => {
              setBulkResult(null);
              setShowContactBulk(true);
            }}
          >
            <Upload size={16} /> Carga masiva
          </button>
          <button
            type="button"
            title="Descargar plantilla"
            style={{
              ...btnSecondary(ui, isDarkMode),
              borderRadius: '0 8px 8px 0',
              borderLeft: 'none',
              width: 34,
              padding: 0,
              justifyContent: 'center',
            }}
            onClick={() => downloadFinancingContactsTemplate({ parentLabel: entityLabel })}
          >
            <ChevronDown size={16} />
          </button>
        </div>
        {selectedContactIds.length ? (
          <button
            type="button"
            style={{ ...btnSecondary(ui, isDarkMode), color: '#dc2626' }}
            onClick={async () => {
              if (!window.confirm(`¿Eliminar ${selectedContactIds.length} contacto(s)?`)) return;
              await api.bulkDeleteContacts(selectedContactIds);
              setSelectedContactIds([]);
              await reloadAndNotify();
            }}
          >
            <Trash2 size={16} /> Eliminar
          </button>
        ) : null}
      </>
    ) : null;

  const listSubtitle = loading
    ? 'Cargando…'
    : listTab === 'entities'
      ? `${filteredEntities.length} registros`
      : listTab === 'contacts'
        ? `${filteredContacts.length} contactos`
        : listTab === 'rounds'
          ? `${fundraising.rounds.length} rondas`
          : listTab === 'investments'
            ? `${fundraising.investments.length} inversiones`
            : listTab === 'cap-table'
              ? `${fundraising.capTable.length} entradas`
              : listTab === 'seguimiento'
                ? `${tracking.length} actividades`
                : `${entities.length} en pipeline`;

  return (
    <div>
      {notice ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${ui.border}`,
            background: isDarkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4',
            color: ui.text,
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} style={{ border: 'none', background: 'transparent', color: ui.muted, cursor: 'pointer' }}>
            Cerrar
          </button>
        </div>
      ) : null}

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>{entitiesLabel}</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
          <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>{headerToolbar}</div>
        </div>

        <div
          role="tablist"
          aria-label={`Vistas de ${entitiesLabel}`}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {listTabs.map((item) => {
            const active = listTab === item.key;
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon size={14} />
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'entities' ? (
          <SimpleTable
            hideChrome
            title={entitiesLabel}
            subtitle={listSubtitle}
            columns={entityColumns}
            rows={filteredEntities}
            loading={loading}
            error={error}
            isDarkMode={isDarkMode}
            ui={ui}
            search={searchEntities}
            onSearch={setSearchEntities}
            selectedIds={selectedEntityIds}
            onToggleSelect={setSelectedEntityIds}
            onRowClick={openDetail}
          />
        ) : null}

        {listTab === 'contacts' ? (
          <SimpleTable
            hideChrome
            title="Contactos"
            subtitle={listSubtitle}
            columns={contactColumns}
            rows={filteredContacts}
            loading={loading}
            error={error}
            isDarkMode={isDarkMode}
            ui={ui}
            search={searchContacts}
            onSearch={setSearchContacts}
            selectedIds={selectedContactIds}
            onToggleSelect={setSelectedContactIds}
            onRowClick={openEditContact}
          />
        ) : null}

        {!isFund && (listTab === 'rounds' || listTab === 'investments' || listTab === 'cap-table') ? (
          <FinancingFundraisingViews
            tab={listTab}
            investors={entities}
            isDarkMode={isDarkMode}
            ui={ui}
            onDataMutated={async () => {
              await reloadAndNotify();
            }}
            fundraising={fundraising}
          />
        ) : null}

        {listTab === 'seguimiento' ? (
          <SimpleTable
            hideChrome
            showSearch={false}
            title="Seguimiento"
            subtitle={listSubtitle}
            columns={[
              { key: 'due_state', label: 'Estado' },
              { key: 'action', label: 'Acción' },
              { key: 'parent_name', label: entityLabel },
              { key: 'contact_name', label: 'Contacto' },
              ...(!isFund
                ? [
                    {
                      key: 'investment_round',
                      label: 'Ronda',
                      render: (r) => r.investment_round || '—',
                    },
                    {
                      key: 'entry_amount',
                      label: 'Monto entrada',
                      render: (r) =>
                        Number(r.entry_amount) ? formatMoney(r.entry_amount) : '—',
                    },
                  ]
                : []),
              {
                key: 'date',
                label: 'Fecha',
                render: (r) =>
                  String(r.follow_up_reminder_date || r.next_step_date || r.activity_date || '').slice(0, 10) ||
                  '—',
              },
              { key: 'owner_name', label: 'Responsable' },
            ]}
            rows={tracking}
            loading={loading}
            error={error}
            isDarkMode={isDarkMode}
            ui={ui}
            search=""
            onSearch={() => {}}
            onRowClick={(row) => {
              const ent = entities.find((e) => Number(e.id) === Number(row.parent_id));
              if (ent) openDetail(ent);
            }}
          />
        ) : null}

        {listTab === 'pipeline' ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(pipelineColumns.length, 1)}, minmax(180px, 1fr))`,
              gap: 10,
              overflowX: 'auto',
              padding: 12,
            }}
          >
            {pipelineColumns.map((col) => (
              <div
                key={col.value}
                style={{
                  border: `1px solid ${ui.border}`,
                  borderRadius: 12,
                  background: isDarkMode ? '#18181b' : '#fff',
                  minHeight: 280,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    padding: '10px 12px',
                    borderBottom: `1px solid ${ui.border}`,
                    borderTop: `3px solid ${col.color || '#6b7280'}`,
                    fontWeight: 700,
                    fontSize: 13,
                    color: ui.text,
                  }}
                >
                  {col.label}{' '}
                  <span style={{ color: ui.muted, fontWeight: 500 }}>({col.cards.length})</span>
                </div>
                <div
                  style={{
                    padding: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    flex: 1,
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                  }}
                >
                  {col.cards.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => openDetail(card)}
                      style={{
                        textAlign: 'left',
                        border: `1px solid ${ui.border}`,
                        borderRadius: 8,
                        padding: 10,
                        height: 76,
                        minHeight: 76,
                        maxHeight: 76,
                        flex: '0 0 76px',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        background: isDarkMode ? '#09090b' : '#f8fafc',
                        cursor: 'pointer',
                        color: ui.text,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {card.name}
                      </div>
                      {!isFund && (card.entry_round || Number(card.committed_amount)) ? (
                        <div style={{ fontSize: 11, color: ui.text, marginTop: 4, fontWeight: 600 }}>
                          {[
                            card.entry_round ? `Ronda ${card.entry_round}` : null,
                            Number(card.committed_amount)
                              ? formatMoney(card.committed_amount)
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: ui.muted, marginTop: 4 }}>
                          {formatMoney(card.target_amount)}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          color: ui.muted,
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minHeight: 14,
                        }}
                      >
                        {card.entity_type || '\u00A0'}
                      </div>
                    </button>
                  ))}
                  {!col.cards.length ? (
                    <div style={{ fontSize: 12, color: ui.muted, padding: 8 }}>Sin tarjetas</div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>


      <FormDrawer
        isOpen={showEntityForm}
        onClose={() => !savingEntity && setShowEntityForm(false)}
        onSave={saveEntity}
        isSaving={savingEntity}
        isDarkMode={isDarkMode}
        title={editingEntityId ? `Editar ${entityLabel.toLowerCase()}` : `Nuevo ${entityLabel.toLowerCase()}`}
        formData={entityForm}
        onChange={(key, value) => setEntityForm((prev) => ({ ...prev, [key]: value }))}
        formError={entityFormError}
        sections={entityFormSections}
        width={580}
        zIndex={10000}
      />

      <FormDrawer
        isOpen={showContactForm}
        onClose={() => !savingContact && setShowContactForm(false)}
        onSave={saveContact}
        isSaving={savingContact}
        isDarkMode={isDarkMode}
        title={editingContactId ? 'Editar contacto' : 'Nuevo contacto'}
        formData={contactForm}
        onChange={(key, value) => setContactForm((prev) => ({ ...prev, [key]: value }))}
        formError={contactFormError}
        sections={contactFormSections}
        zIndex={10000}
      />

      <FormDrawer
        isOpen={showActivityForm}
        onClose={() => !savingActivity && setShowActivityForm(false)}
        onSave={saveActivity}
        isSaving={savingActivity}
        isDarkMode={isDarkMode}
        title="Nueva actividad"
        formData={activityForm}
        onChange={(key, value) => setActivityForm((prev) => ({ ...prev, [key]: value }))}
        formError={activityError}
        sections={activityFormSections}
        zIndex={10050}
      />

      {detailEntity ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10020,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setDetailEntity(null)}
        >
          <div
            style={{
              width: 'min(720px, 100%)',
              maxHeight: '90vh',
              overflow: 'auto',
              background: isDarkMode ? '#09090b' : '#fff',
              borderRadius: 12,
              border: `1px solid ${ui.border}`,
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: ui.text }}>{detailEntity.name}</h3>
                <p style={{ margin: '4px 0 0', color: ui.muted, fontSize: 13 }}>
                  {detailEntity.funnel_stage} · {formatMoney(detailEntity.target_amount)}
                  {detailEntity.entity_type ? ` · ${detailEntity.entity_type}` : ''}
                </p>
                {!isFund && (detailEntity.entry_round || Number(detailEntity.committed_amount)) ? (
                  <p style={{ margin: '6px 0 0', color: ui.text, fontSize: 13, fontWeight: 600 }}>
                    Entró
                    {detailEntity.entry_round ? ` en ${detailEntity.entry_round}` : ''}
                    {Number(detailEntity.committed_amount)
                      ? ` por ${formatMoney(detailEntity.committed_amount)}`
                      : ''}
                  </p>
                ) : null}
                {!isFund && (detailEntity.sectors || detailEntity.preferred_stages || detailEntity.ticket_min || detailEntity.ticket_max) ? (
                  <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: 12, lineHeight: 1.4 }}>
                    {[
                      detailEntity.preferred_stages
                        ? `Tesis: ${detailEntity.preferred_stages}`
                        : '',
                      detailEntity.sectors,
                      detailEntity.lead_role,
                      Number(detailEntity.ticket_min) || Number(detailEntity.ticket_max)
                        ? `Ticket ${formatMoney(detailEntity.ticket_min)} – ${formatMoney(detailEntity.ticket_max)}`
                        : '',
                      detailEntity.geography,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={btnSecondary(ui, isDarkMode)} onClick={() => openEditEntity(detailEntity)}>
                  Editar
                </button>
                <button
                  type="button"
                  style={btnPrimary}
                  onClick={() => {
                    setActivityForm({
                      ...EMPTY_ACTIVITY,
                      activity_date: new Date().toISOString().slice(0, 10),
                      funnel_stage: detailEntity.funnel_stage || '',
                      investment_round: detailEntity.entry_round || '',
                      entry_amount: detailEntity.committed_amount
                        ? String(detailEntity.committed_amount)
                        : '',
                    });
                    setActivityError('');
                    setShowActivityForm(true);
                  }}
                >
                  <Plus size={16} /> Actividad
                </button>
                <button
                  type="button"
                  onClick={() => setDetailEntity(null)}
                  style={{ border: 'none', background: 'transparent', color: ui.muted, cursor: 'pointer', fontSize: 18 }}
                >
                  ×
                </button>
              </div>
            </div>
            <h4 style={{ margin: '0 0 8px', color: ui.text, fontSize: 14 }}>Actividades</h4>
            {detailActivities.length === 0 ? (
              <p style={{ color: ui.muted, fontSize: 13 }}>Sin actividades aún.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {detailActivities.map((act) => (
                  <div
                    key={act.id}
                    style={{
                      border: `1px solid ${ui.border}`,
                      borderRadius: 8,
                      padding: 10,
                      background: isDarkMode ? '#18181b' : '#f8fafc',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: ui.text, fontSize: 13 }}>
                      {act.action || 'Actividad'} · {act.status}
                    </div>
                    <div style={{ fontSize: 12, color: ui.muted, marginTop: 2 }}>
                      {String(act.activity_date || '').slice(0, 10) || 'Sin fecha'}
                      {act.contact_name ? ` · ${act.contact_name}` : ''}
                      {act.funnel_stage ? ` · ${act.funnel_stage}` : ''}
                    </div>
                    {act.investment_round || Number(act.entry_amount) ? (
                      <div style={{ fontSize: 12, color: ui.text, marginTop: 4, fontWeight: 600 }}>
                        {[
                          act.investment_round ? `Ronda ${act.investment_round}` : null,
                          Number(act.entry_amount) ? formatMoney(act.entry_amount) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    ) : null}
                    {act.notes ? (
                      <div style={{ fontSize: 12, color: ui.text, marginTop: 6 }}>{act.notes}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <FinancingEntitiesBulkImportModal
        isOpen={showEntityBulk}
        onClose={() => !importing && setShowEntityBulk(false)}
        isImporting={importing}
        isDarkMode={isDarkMode}
        result={bulkResult}
        trackKey={trackKey}
        entityNoun={entitiesLabel.toLowerCase()}
        onImport={async (rows) => {
          setImporting(true);
          try {
            const data = await api.bulkImport(
              rows.map((row) => ({
                ...row,
                target_amount: Number(row.target_amount || 0),
                committed_amount: Number(row.committed_amount || 0),
              }))
            );
            setBulkResult(data);
            await reloadAndNotify();
            const created = Number(data?.created || 0);
            const skipped = Number(data?.skipped || 0);
            if (created > 0 || Number(data?.errors || 0) === 0) {
              setNotice(
                `Importación exitosa: ${created} creados` + (skipped ? `, ${skipped} omitidos` : '') + '.'
              );
            }
            return data;
          } finally {
            setImporting(false);
          }
        }}
      />

      <FinancingContactsBulkImportModal
        isOpen={showContactBulk}
        onClose={() => !importing && setShowContactBulk(false)}
        isImporting={importing}
        isDarkMode={isDarkMode}
        result={bulkResult}
        parentLabel={entityLabel}
        onImport={async (rows) => {
          setImporting(true);
          try {
            const data = await api.bulkImportContacts(rows);
            setBulkResult(data);
            await reloadAndNotify();
            const created = Number(data?.created || 0);
            const skipped = Number(data?.skipped || 0);
            if (created > 0 || Number(data?.errors || 0) === 0) {
              setNotice(
                `Importación exitosa: ${created} contacto(s)` +
                  (skipped ? `, ${skipped} omitidos` : '') +
                  '.'
              );
            }
            return data;
          } finally {
            setImporting(false);
          }
        }}
      />
    </div>
  );
};

export default FinancingTrackPanel;
